<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="hide"
    width="500"
    v-if="role('admin', 'departmentManager', 'manager', 'safetyRepresentative')"
    data-test="change-position-dialog"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-on="on"
        small
        class="primary accent-4 white--text mr-2 my-1"
        data-test="change-position-button"
      >
        {{ $t("profile.assignment.changePositions.button") }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline grey lighten-2" primary-title>{{
        $t("profile.assignment.changePositions.dialog")
      }}</v-card-title>

      <v-card-text data-test="change-position-dialog-list">
        <v-checkbox
          v-model="loadWithoutParent"
          hide-details
          dense
          :label="$t('profile.assignment.changePositions.loadWithoutParent')"
          class="mb-2"
        />
        <v-autocomplete
          class="mt-0 pt-0"
          v-model="newPositions"
          :items="allPositionTagsCalc"
          required
          chips
          deletable-chips
          :item-text="getTagDescription"
          return-object
          multiple
          data-test="change-position-dialog-items"
          :filter="customFilter"
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              close
              :class="boldIfEmployeeDepartments(data.item.parent)"
              @click="removeChip(data.item)"
              @click:close="removeChip(data.item)"
            >
              {{ getTagDescription(data.item) }}
            </v-chip>
          </template>
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item
              :three-line="!!item.explanation"
              v-bind="attrs"
              v-on="on"
            >
              <v-list-item-content>
                <v-list-item-title
                  :class="boldIfEmployeeDepartments(item.parent)"
                  >{{ getTagDescription(item) }}</v-list-item-title
                >
                <v-list-item-subtitle v-if="item.explanation">
                  <span> {{ item.explanation }}</span>
                </v-list-item-subtitle>
                <v-divider />
                <v-list-item-subtitle
                  v-if="item.parent"
                  :style="
                    employeeDepartments
                      .filter((x) => x.source == 'kronos')
                      .some((x) => `${x.id}` == item.parent.id)
                      ? 'text-decoration: underline;'
                      : ''
                  "
                >
                  {{ getTagDescription(item.parent) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="grey" text @click="hide">{{ $t("dialog.cancel") }}</v-btn>

        <v-btn
          color="primary"
          text
          @click="save"
          :loading="loading"
          data-test="change-position-dialog-change"
          >{{ $t("dialog.assign") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TagsQuery from "@/graphql/queries/TagsWithParent.gql";
import ChangeEmployeeTagsQuery from "@/graphql/ChangeEmployeeTags.gql";
import { mapGetters } from "vuex";

import { getTagDescription } from "@/utils/tag";

export default {
  props: ["id", "employeeTags"],

  data() {
    return {
      show: false,
      newPositions: [],
      loadWithoutParent: false,
      loading: false,
    };
  },

  mounted() {
    this.newPositions = this.employeeTags.filter(
      (t) => t.category === "position"
    );
  },

  computed: {
    ...mapGetters(["role"]),
    allPositionTagsCalc() {
      if (!this.allPositionTags) return [];
      if (this.loadWithoutParent) {
        return this.allPositionTags.concat(this.newPositions);
      } else {
        return this.allPositionTags
          .filter(
            (x) =>
              x.parent &&
              this.employeeDepartments.some((y) => y.id == x.parent.id)
          )
          .concat(this.newPositions);
      }
    },
    employeeDepartments() {
      return (
        (this.employeeTags &&
          this.employeeTags.length &&
          this.employeeTags.filter((x) => x.category == "department")) ||
        []
      );
    },
  },

  methods: {
    getTagDescription: getTagDescription,
    boldIfEmployeeDepartments(parent) {
      return parent && this.employeeDepartments.some((x) => x.id == parent.id)
        ? "font-weight-bold"
        : "";
    },
    removeChip(item) {
      const index = this.newPositions.findIndex((nt) => nt.id == item.id);
      if (index >= 0) this.newPositions.splice(index, 1);
    },
    hide() {
      this.show = false;
    },
    save() {
      this.loading = true;
      const tagIDs = this.newPositions.map((e) => e.id);
      this.$apollo
        .mutate({
          mutation: ChangeEmployeeTagsQuery,
          variables: {
            id: this.id,
            tags: tagIDs,
            category: "position",
          },
        })
        .then(() => {
          const newDepartments = this.getNewDepartments(this.newPositions);
          this.updateDepartments(newDepartments);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getNewDepartments(newPositions) {
      const assignedPositionTagsWithDepartmentParent = newPositions.filter(
        (x) => x.parent && x.parent.category == "department"
      );

      const departmentsFromPositions = assignedPositionTagsWithDepartmentParent
        .map((x) => x.parent)
        .filter((v, i, a) => a.findIndex((t) => t.id == v.id) == i);

      const items = departmentsFromPositions
        .concat(this.employeeDepartments)
        .filter((v, i, a) => a.findIndex((t) => t.id == v.id) == i);

      return items;
    },
    updateDepartments(departments) {
      if (!departments) {
        this.$emit("refresh");
        this.hide();
        return;
      }

      const newDepartments = departments.filter(
        (dep) => !this.employeeDepartments.map((x) => x.id).includes(dep.id)
      );

      if (newDepartments.length == 0) {
        this.$emit("refresh");
        this.hide();
        return;
      }
      const newDepartmentsText = newDepartments
        .map((t) => this.getTagDescription(t))
        .join(", ");

      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ChangeEmployeeTagsQuery,
          variables: {
            id: this.id,
            tags: departments.map((t) => t.id),
            category: "department",
          },
        })
        .then(() => {
          this.$store.commit("push_global_message", {
            color: "success",
            text: this.$t("tags.add.added", {
              text: newDepartmentsText,
            }),
          });
          this.$emit("refresh");
          this.hide();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    customFilter(item, queryText) {
      const searchText = queryText.toLowerCase();
      let text = `${item.name} | ${item.description || ""}`;
      if (item.parent) {
        text += ` | ${item.parent.name} | ${item.parent.description || ""}`;
      }
      return text.toLowerCase().includes(searchText);
    },
  },

  apollo: {
    allPositionTags: {
      query: TagsQuery,
      update(data) {
        return data.tags.filter((t) => t.category === "position");
      },
      skip() {
        return !this.show;
      },
      fetchPolicy: "cache-and-network",
    },
  },
};
</script>
